<template>
	<div
		ref="mapWrapperRef"
		:style="{ height: '100%', width: '100%', opacity: ready ? '100' : '0' }"
	/>
	<slot></slot>
</template>

<script lang="ts">
import { defineComponent, ref, toRef, watch, type PropType } from "vue";
import { useEventsBinding } from "../composable/events-binding";
import { provideMap } from "../composable/google-map";
import { usePropsBinding } from "../composable/props-binding";

const events = [
	"click",
	"dblclick",
	"drag",
	"dragend",
	"dragstart",
	"idle",
	"mousemove",
	"mouseout",
	"mouseover",
	"rightclick",
	"tilesloaded",
	"center_changed",
	"bounds_changed",
	"zoom_changed",
	"maptypeid_changed",
] as const;

export default defineComponent({
	name: "GoogleMap",
	inheritAttrs: false,
	props: {
		mapId: {
			type: String,
			required: true,
		},
		center: Object as PropType<google.maps.LatLng>,
		zoom: Number,
		mapTypeId: String,
		options: Object as PropType<google.maps.MapOptions>,
	},
	emits: [...events, "ready", "update:center", "update:zoom", "update:mapTypeId"],
	setup(props, { emit }) {
		const { bindEvents } = useEventsBinding(events);
		const { bindProps } = usePropsBinding(props);

		const mapWrapperRef = ref<HTMLDivElement>();

		const ready = ref(false);
		const { map, isReady } = provideMap(toRef(props, "mapId"));

		// Mount the map to the DOM when it change
		watch(
			[map, mapWrapperRef],
			() => {
				if (map.value == null || mapWrapperRef.value == null) return;

				mapWrapperRef.value!.appendChild(map.value!.getDiv());

				map.value.setOptions({
					...props,
					...props.options,
				});

				bindProps(map.value);
				bindEvents(map.value);

				google.maps.event.addListenerOnce(map.value, "idle", () => {
					setTimeout(() => {
						isReady();
						emit("ready", map.value);
						ready.value = true;
					});
				});
			},
			{ immediate: true }
		);

		return { mapWrapperRef, ready };
	},
});
</script>
